import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Slider } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { getScaleValueFromElement } from '~/common/editor/helpers';
import useElement from '../../hooks/useElement';
import useDesignElementContentTransform from '../../hooks/useDesignElementContentTransform';

function useScale(id) {
    const { key, element, updateElement } = useElement(id);
    const { naturalDimension, dimension } = element;
    const contentTransform = useDesignElementContentTransform(id);
    const [value, setValue] = useState(() => Math.round(getScaleValueFromElement(element) * 100));
    const [isDisabled, setIsDisabled] = useState(false);

    const handleOnChange = useCallback(
        (newValue) => {
            setValue(newValue);
            const { scale, horizontalOffset, verticalOffset } = contentTransform;
            const { width: naturalWidth, height: naturalHeight } = naturalDimension;
            const newScale = +(newValue / 100).toFixed(2);

            const param = {
                contentTransform: {
                    ...contentTransform,
                    horizontalOffset: Math.round(
                        horizontalOffset + (naturalWidth * scale - naturalWidth * newScale) / 2,
                    ),
                    verticalOffset: Math.round(verticalOffset + (naturalHeight * scale - naturalHeight * newScale) / 2),
                    scale: newScale,
                },
            };
            updateElement(param);
        },
        [contentTransform, naturalDimension, updateElement],
    );

    const minScale = useMemo(() => {
        if (contentTransform.type === 'COVER') {
            const optimalScale = Math.ceil(
                Math.max(dimension.width / naturalDimension.width, dimension.height / naturalDimension.height) * 100,
            );

            return optimalScale;
        }

        return 0;
    }, [contentTransform.type, dimension.width, dimension.height, naturalDimension.width, naturalDimension.height]);

    useEffect(() => {
        setValue(Math.round(contentTransform.scale * 100));
    }, [key, contentTransform]);

    useEffect(() => {
        setValue(Math.round(getScaleValueFromElement(element) * 100));
    }, [key, element]);

    useEffect(() => {
        setValue((prev) => (prev < minScale ? Math.round(minScale) : prev));
        setIsDisabled(minScale > 200);
    }, [minScale]);

    const scaleValue = value < minScale ? minScale : value;

    return { value: scaleValue, minScale, handleOnChange, isDisabled };
}

const Scale = ({ id, disabled }) => {
    const { translate } = useTranslate();
    const { value, minScale, handleOnChange, isDisabled } = useScale(id);
    const { element } = useElement(id);

    const { contentTransform } = element;

    return (
        <Form.Group>
            <Form.Label>{translate('design.sidebar.scene.options.crop-mode.scale.label')}</Form.Label>
            <Slider
                value={value}
                min={minScale}
                max={200}
                isFromCenter={contentTransform.type === 'FREE'}
                inputPosition="right"
                onChange={handleOnChange}
                isDisabled={disabled || isDisabled}
                isInactive={disabled || isDisabled}
            />
        </Form.Group>
    );
};

export default Scale;
